import { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';

interface BarcodeDetectorLike {
  detect(src: HTMLVideoElement): Promise<{ rawValue: string }[]>;
}

/** Scan a shelf QR code with the phone camera. Uses BarcodeDetector when present, else jsQR. */
export function CameraScan({ onCode, onClose }: { onCode: (code: string) => void; onClose: () => void }) {
  const video = useRef<HTMLVideoElement>(null);
  const [msg, setMsg] = useState('Point the camera at the QR code on the shelf.');
  const dlg = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    dlg.current?.showModal();
    let stream: MediaStream | null = null;
    let stop = false;
    let raf = 0;
    (async () => {
      if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
        setMsg('The camera needs HTTPS. Type the shelf code instead.');
        return;
      }
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      } catch {
        setMsg('The camera is not available here. Allow camera access in the browser, or type the code instead.');
        return;
      }
      if (stop) return stream.getTracks().forEach((t) => t.stop());
      const v = video.current!;
      v.srcObject = stream;
      await v.play().catch(() => undefined);
      const BD = (window as unknown as { BarcodeDetector?: new (o: object) => BarcodeDetectorLike }).BarcodeDetector;
      const bd = BD ? new BD({ formats: ['qr_code'] }) : null;
      const jsQR = bd ? null : (await import('jsqr')).default;
      const c = document.createElement('canvas');
      const ctx = c.getContext('2d', { willReadFrequently: true })!;
      const tick = async () => {
        if (stop) return;
        if (v.readyState >= 2 && v.videoWidth) {
          let code: string | null = null;
          try {
            if (bd) code = (await bd.detect(v))[0]?.rawValue ?? null;
            else if (jsQR) {
              c.width = v.videoWidth;
              c.height = v.videoHeight;
              ctx.drawImage(v, 0, 0);
              const img = ctx.getImageData(0, 0, c.width, c.height);
              code = jsQR(img.data, img.width, img.height)?.data ?? null;
            }
          } catch {
            /* keep trying */
          }
          if (code) {
            stop = true;
            onCode(code);
            return;
          }
        }
        raf = requestAnimationFrame(() => void tick());
      };
      void tick();
    })();
    return () => {
      stop = true;
      cancelAnimationFrame(raf);
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, [onCode]);

  return (
    <dialog ref={dlg} onClose={onClose} onCancel={onClose}>
      <div className="mhead">
        <strong>Scan the shelf QR code</strong>
        <button className="btn small" onClick={onClose}>
          Close
        </button>
      </div>
      <div className="mbody">
        <video ref={video} playsInline muted style={{ width: '100%', borderRadius: 10, background: '#000', maxHeight: '60vh' }} />
        <p className="meta">{msg}</p>
      </div>
    </dialog>
  );
}

export function QrCode({ text, size = 144 }: { text: string; size?: number }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    if (ref.current) void QRCode.toCanvas(ref.current, text, { width: size, margin: 1, errorCorrectionLevel: 'M' });
  }, [text, size]);
  return <canvas ref={ref} width={size} height={size} />;
}
